import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { KeyRound, ArrowLeft, RefreshCw, Clock, User, CheckCircle2, ShieldCheck } from 'lucide-react';
import AuthLayout from './AuthLayout';
import OtpInput, { OTP_LENGTH, EMPTY_OTP, focusOtpBox, formatCountdown } from './OtpInput';
import PasswordInput from './PasswordInput';
import PasswordRequirements from './PasswordRequirements';
import authApi from '../api/authApi';
import settingsApi from '../api/settingsApi';
import { isPasswordValid, passwordError, PASSWORDS_DO_NOT_MATCH } from '../utils/passwordPolicy';

// The pending request (opaque challenge token + timers, never the OTP) is
// kept in sessionStorage so a refresh of the OTP step keeps working. The
// reset token issued after OTP verification is held in memory only.
const PENDING_RESET_KEY = 'pendingPasswordReset';

const readPending = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_RESET_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writePending = (value) => {
  try {
    if (value) sessionStorage.setItem(PENDING_RESET_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PENDING_RESET_KEY);
  } catch (e) {
    // storage unavailable - in-memory state still works for this page
  }
};

const REDIRECT_DELAY_MS = 4000;

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [featureEnabled, setFeatureEnabled] = useState(null); // null = loading
  const [pending, setPendingState] = useState(readPending);
  const [step, setStep] = useState(() => (readPending() ? 'otp' : 'request'));

  // Step 1
  const [identifier, setIdentifier] = useState('');
  const [identifierError, setIdentifierError] = useState('');
  const [requesting, setRequesting] = useState(false);

  // Step 2
  const [digits, setDigits] = useState(EMPTY_OTP);
  const [otpError, setOtpError] = useState('');
  const [needsNewCode, setNeedsNewCode] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const inputsRef = useRef([]);

  // Step 3
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [resetting, setResetting] = useState(false);

  const [now, setNow] = useState(Date.now());

  const setPending = (value) => {
    writePending(value);
    setPendingState(value);
  };

  useEffect(() => {
    let cancelled = false;
    settingsApi.getForgotPassword()
      .then((response) => { if (!cancelled) setFeatureEnabled(Boolean(response.data?.data?.enabled)); })
      // If the flag can't be read, show the form; the API still enforces it.
      .catch(() => { if (!cancelled) setFeatureEnabled(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (step === 'otp') inputsRef.current[0]?.focus();
  }, [step]);

  useEffect(() => {
    if (step !== 'done') return undefined;
    const id = setTimeout(() => navigate('/login', { replace: true }), REDIRECT_DELAY_MS);
    return () => clearTimeout(id);
  }, [step, navigate]);

  const startOver = (message) => {
    if (message) toast.error(message);
    setPending(null);
    setResetToken(null);
    setDigits(EMPTY_OTP);
    setOtpError('');
    setNeedsNewCode(false);
    setNewPassword('');
    setConfirmPassword('');
    setTouched({});
    setServerErrors({});
    setStep('request');
  };

  const backToLogin = () => {
    setPending(null);
    navigate('/login', { replace: true });
  };

  const handleDisabled = (data) => {
    if (data?.code === 'FORGOT_PASSWORD_DISABLED') {
      setPending(null);
      setFeatureEnabled(false);
      return true;
    }
    return false;
  };

  // ---- Step 1: request OTP -------------------------------------------------
  const handleRequest = async (e) => {
    e.preventDefault();
    const value = identifier.trim();
    if (!value) {
      setIdentifierError('Please enter your username or email address.');
      return;
    }
    setIdentifierError('');
    setRequesting(true);
    try {
      const { data } = await authApi.forgotPassword(value);
      setPending({
        challengeToken: data.challenge_token,
        otpExpiresAt: Date.now() + data.otp_expires_in * 1000,
        resendAvailableAt: Date.now() + data.resend_available_in * 1000
      });
      setDigits(EMPTY_OTP);
      setOtpError('');
      setNeedsNewCode(false);
      setStep('otp');
      toast.info(data.message);
    } catch (error) {
      const data = error.response?.data;
      if (!handleDisabled(data)) {
        setIdentifierError(data?.message || 'Unable to process the request. Please try again.');
      }
    } finally {
      setRequesting(false);
    }
  };

  // ---- Step 2: verify OTP --------------------------------------------------
  const otp = digits.join('');
  const expiresInMs = pending ? pending.otpExpiresAt - now : 0;
  const resendInMs = pending ? pending.resendAvailableAt - now : 0;
  const otpExpired = Boolean(pending) && expiresInMs <= 0;
  const canResend = Boolean(pending) && resendInMs <= 0 && !resending;
  const canVerify = otp.length === OTP_LENGTH && !verifying && !needsNewCode && !otpExpired;

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setOtpError('Please enter the 6-digit verification code.');
      return;
    }
    setVerifying(true);
    setOtpError('');
    try {
      const { data } = await authApi.forgotPasswordVerifyOtp(pending.challengeToken, otp);
      setResetToken(data.reset_token);
      setPending(null);
      setStep('reset');
      toast.success(data.message || 'OTP verified successfully.');
    } catch (error) {
      const data = error.response?.data || {};
      if (handleDisabled(data)) return;
      if (data.code === 'INVALID_CHALLENGE') {
        startOver(data.message);
        return;
      }
      if (data.code === 'OTP_BLOCKED' || data.code === 'OTP_EXPIRED') setNeedsNewCode(true);
      const remaining = data.attempts_remaining !== undefined
        ? ` ${data.attempts_remaining} attempt${data.attempts_remaining === 1 ? '' : 's'} remaining.`
        : '';
      setOtpError(`${data.message || 'Verification failed. Please try again.'}${remaining}`);
      setDigits(EMPTY_OTP);
      focusOtpBox(inputsRef, 0);
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setResending(true);
    try {
      const { data } = await authApi.forgotPasswordResendOtp(pending.challengeToken);
      setPending({
        ...pending,
        otpExpiresAt: Date.now() + data.otp_expires_in * 1000,
        resendAvailableAt: Date.now() + data.resend_available_in * 1000
      });
      setDigits(EMPTY_OTP);
      setOtpError('');
      setNeedsNewCode(false);
      focusOtpBox(inputsRef, 0);
      toast.success(data.message);
    } catch (error) {
      const data = error.response?.data || {};
      if (handleDisabled(data)) return;
      if (data.code === 'INVALID_CHALLENGE' || data.code === 'RESEND_LIMIT') {
        startOver(data.message);
        return;
      }
      if (data.code === 'RESEND_TOO_SOON' && data.retry_after) {
        setPending({ ...pending, resendAvailableAt: Date.now() + data.retry_after * 1000 });
      }
      toast.error(data.message || 'Could not resend the code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  // ---- Step 3: new password ------------------------------------------------
  const newPasswordError = serverErrors.new_password
    || (touched.new_password && newPassword ? passwordError(newPassword) : '')
    || (touched.new_password && !newPassword ? 'Password is required.' : '');
  const confirmError = serverErrors.confirm_password
    || (touched.confirm_password && confirmPassword && confirmPassword !== newPassword ? PASSWORDS_DO_NOT_MATCH : '')
    || (touched.confirm_password && !confirmPassword ? 'Please confirm the new password.' : '');
  const canReset = isPasswordValid(newPassword) && confirmPassword === newPassword && !resetting;

  const handleReset = async (e) => {
    e.preventDefault();
    setTouched({ new_password: true, confirm_password: true });
    if (!canReset) return;
    setResetting(true);
    setServerErrors({});
    try {
      await authApi.forgotPasswordReset(resetToken, newPassword, confirmPassword);
      setResetToken(null);
      setNewPassword('');
      setConfirmPassword('');
      setStep('done');
    } catch (error) {
      const data = error.response?.data || {};
      if (handleDisabled(data)) return;
      if (data.code === 'INVALID_RESET_TOKEN') {
        startOver(data.message);
        return;
      }
      if (data.field) setServerErrors({ [data.field]: data.message });
      else toast.error(data.message || 'Unable to reset password. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  // ---- Render --------------------------------------------------------------
  const primaryButton = (enabled) => ({
    width: '100%',
    padding: '0.8rem',
    background: enabled ? 'linear-gradient(90deg, #1642b0, #4f8ef7)' : '#9ca3af',
    color: 'white',
    border: 'none',
    borderRadius: '0.6rem',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: enabled ? 'pointer' : 'not-allowed'
  });

  const header = (Icon, title, text) => (
    <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
      <div style={{
        width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#dbeafe',
        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem'
      }}>
        <Icon size={28} color="#1d4ed8" />
      </div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.5rem' }}>{title}</h2>
      {text && <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>{text}</p>}
    </div>
  );

  const backLink = (
    <div style={{ textAlign: 'center', marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #f1f5f9' }}>
      <button type="button" className="fp-link-btn" onClick={backToLogin} style={{ color: '#6b7280', cursor: 'pointer', fontWeight: 500 }}>
        <ArrowLeft size={15} /> Back to sign in
      </button>
    </div>
  );

  let content;
  if (featureEnabled === null) {
    content = <p style={{ textAlign: 'center', color: '#6b7280', margin: 0 }}>Loading…</p>;
  } else if (!featureEnabled) {
    content = (
      <>
        {header(KeyRound, 'Forgot Password', 'Forgot password is currently disabled. Please contact your administrator to reset your password.')}
        {backLink}
      </>
    );
  } else if (step === 'request') {
    content = (
      <>
        {header(KeyRound, 'Forgot Password', 'Enter your username or email address and we will send a 6-digit verification code to your registered email address.')}
        <form onSubmit={handleRequest} noValidate>
          <label htmlFor="fp-identifier" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
            Username or Email<span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
          </label>
          <div style={{ position: 'relative', marginBottom: identifierError ? '0.3rem' : '1.25rem' }}>
            <User aria-hidden="true" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1.1rem', height: '1.1rem', color: '#9ca3af' }} />
            <input
              id="fp-identifier"
              type="text"
              value={identifier}
              onChange={(e) => { setIdentifier(e.target.value); if (identifierError) setIdentifierError(''); }}
              placeholder="Enter username or email"
              autoComplete="username"
              autoFocus
              aria-invalid={Boolean(identifierError)}
              className="password-input"
              style={{
                width: '100%',
                padding: '0.7rem 0.75rem 0.7rem 2.4rem',
                border: `1px solid ${identifierError ? '#dc2626' : '#d1d5db'}`,
                borderRadius: '0.6rem',
                fontSize: '0.95rem',
                backgroundColor: identifierError ? '#fef2f2' : 'white',
                boxSizing: 'border-box'
              }}
            />
          </div>
          {identifierError && <div role="alert" style={{ marginBottom: '1.25rem', fontSize: '0.75rem', color: '#dc2626' }}>{identifierError}</div>}
          <button type="submit" disabled={requesting} className="fp-primary-btn" style={primaryButton(!requesting)}>
            {requesting ? 'Sending...' : 'Send OTP'}
          </button>
        </form>
        {backLink}
      </>
    );
  } else if (step === 'otp') {
    content = (
      <>
        {header(ShieldCheck, 'Verify OTP', 'A 6-digit verification code has been sent to your registered email address. Please enter it below.')}
        <form onSubmit={handleVerify} noValidate>
          <label style={{ display: 'block', textAlign: 'center', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.75rem' }}>
            Enter OTP
          </label>
          <OtpInput
            digits={digits}
            setDigits={setDigits}
            inputsRef={inputsRef}
            disabled={verifying || needsNewCode}
            hasError={Boolean(otpError)}
            onEdit={() => setOtpError('')}
          />
          <div style={{ minHeight: '1.25rem', textAlign: 'center', marginBottom: '0.75rem' }}>
            {otpError ? (
              <span role="alert" style={{ fontSize: '0.8rem', color: '#dc2626' }}>{otpError}</span>
            ) : (
              <span style={{ fontSize: '0.8rem', color: otpExpired ? '#dc2626' : '#6b7280', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                <Clock size={13} />
                {otpExpired ? 'The verification code has expired. Please request a new OTP.' : `Code expires in ${formatCountdown(expiresInMs)}`}
              </span>
            )}
          </div>
          <button type="submit" disabled={!canVerify} className="fp-primary-btn" style={primaryButton(canVerify)}>
            {verifying ? 'Verifying...' : 'Verify OTP'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
          <div style={{ marginBottom: '0.4rem' }}>Didn't receive the code?</div>
          <button
            type="button"
            className="fp-link-btn"
            onClick={handleResend}
            disabled={!canResend}
            style={{ color: canResend ? '#2563eb' : '#9ca3af', cursor: canResend ? 'pointer' : 'not-allowed' }}
          >
            <RefreshCw size={15} />
            {resending ? 'Sending...' : resendInMs > 0 ? `Resend OTP in ${Math.ceil(resendInMs / 1000)}s` : 'Resend OTP'}
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
          <button type="button" className="fp-link-btn" onClick={() => startOver()} style={{ color: '#6b7280', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}>
            Use a different username or email
          </button>
        </div>
        {backLink}
      </>
    );
  } else if (step === 'reset') {
    content = (
      <>
        {header(KeyRound, 'Create New Password', 'Choose a new password for your account.')}
        <form onSubmit={handleReset} noValidate>
          <PasswordInput
            id="fp-new-password"
            label="New Password"
            value={newPassword}
            autoFocus
            onChange={(e) => { setNewPassword(e.target.value); setServerErrors({}); }}
            onBlur={() => setTouched((t) => ({ ...t, new_password: true }))}
            error={newPasswordError}
            placeholder="Enter new password"
          />
          <PasswordRequirements password={newPassword} />
          <PasswordInput
            id="fp-confirm-password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setServerErrors({}); setTouched((t) => ({ ...t, confirm_password: true })); }}
            onBlur={() => setTouched((t) => ({ ...t, confirm_password: true }))}
            error={confirmError}
            placeholder="Re-enter new password"
          />
          <button type="submit" disabled={!canReset} className="fp-primary-btn" style={primaryButton(canReset)}>
            {resetting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
        {backLink}
      </>
    );
  } else {
    content = (
      <>
        {header(CheckCircle2, 'Password Reset', 'Your password has been reset successfully. Please login with your new password.')}
        <button type="button" className="fp-primary-btn" onClick={() => navigate('/login', { replace: true })} style={primaryButton(true)}>
          Back to Sign In
        </button>
        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.75rem', marginBottom: 0 }}>
          Redirecting to sign in…
        </p>
      </>
    );
  }

  return (
    <AuthLayout>
      <style>{`
        .fp-card {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 440px;
          background: white;
          border-radius: 1.25rem;
          box-shadow: 0 25px 60px -15px rgba(22, 66, 176, 0.55);
          padding: 2.25rem 2rem;
          overflow: hidden;
          box-sizing: border-box;
        }
        .fp-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, #60a5fa, #1642b0, #60a5fa);
        }
        .fp-primary-btn { transition: filter 0.15s ease; }
        .fp-primary-btn:hover:not(:disabled) { filter: brightness(1.06); }
        .fp-link-btn {
          background: none;
          border: none;
          padding: 0;
          font-size: 0.9rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
        }
        @media (max-width: 480px) {
          .fp-card { padding: 1.75rem 1.1rem; }
        }
      `}</style>
      <div className="fp-card">{content}</div>
    </AuthLayout>
  );
};

export default ForgotPassword;
